import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import DateFnsAdapter from '@date-io/date-fns';
import { FloatingFocusManager, FloatingOverlay, autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { TextField } from '../TextField/TextField';
import { Tooltip } from '../Tooltip/Tooltip';
export const RelativeDateOptions = [
    'TODAY',
    'YESTERDAY',
    'THIS_WEEK',
    'LAST_WEEK',
    'THIS_MONTH',
    'LAST_MONTH',
    'THIS_YEAR',
    'LAST_YEAR',
    'LAST_7_DAYS',
    'LAST_30_DAYS',
    'CUSTOM',
];
export const RelativeDateOptionLabels = {
    TODAY: 'Today',
    YESTERDAY: 'Yesterday',
    THIS_WEEK: 'This Week',
    LAST_WEEK: 'Last Week',
    THIS_MONTH: 'This Month',
    LAST_MONTH: 'Last Month',
    THIS_YEAR: 'This Year',
    LAST_YEAR: 'Last Year',
    LAST_7_DAYS: 'Last 7 Days',
    LAST_30_DAYS: 'Last 30 Days',
    CUSTOM: 'Custom',
};
export const RelativeDateOptionUnits = {
    TODAY: 'day',
    YESTERDAY: 'day',
    THIS_WEEK: 'week',
    LAST_WEEK: 'week',
    THIS_MONTH: 'month',
    LAST_MONTH: 'month',
    THIS_YEAR: 'year',
    LAST_YEAR: 'year',
    LAST_7_DAYS: 'day',
    LAST_30_DAYS: 'day',
    CUSTOM: 'day',
};
export function RelativeDatePicker(props) {
    const [selectedRelativeDate, setSelectedRelativeDate] = useState('THIS_MONTH');
    const [dateRange, setDateRange] = useState('');
    const adapter = props.adapter ||
        useMemo(() => {
            return new DateFnsAdapter({
                locale: props.locale,
            });
        }, []);
    const [isOpen, setIsOpen] = useState(false);
    // const [focusedDate, setFocusedDate] = useState(props.value || new Date());
    // const [selectedDate, setSelectedDate] = useState(props.value || null);
    // const daysInMonth: number = adapter.getDaysInMonth(focusedDate);
    // function paddingDays() {
    //   // first get the first day of the month
    //   const startOfMonth = adapter.startOfMonth(focusedDate);
    //   //  get the day of the week
    //   const dayOfWeek = adapter.format(startOfMonth, 'weekdayShort');
    //   // get the index of the day of the week
    //   const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    //   const padding = days.indexOf(dayOfWeek);
    //   return padding;
    // }
    // function isDisabledDate(date: Date) {
    //   if (
    //     (props.minDate && adapter.isBefore(date, props.minDate)) ||
    //     (props.maxDate && adapter.isAfter(date, props.maxDate)) ||
    //     (props.disableWeekends && ['Sat', 'Sun'].includes(adapter.format(date, 'weekdayShort'))) ||
    //     (props.disableDates && props.disableDates.find((d) => adapter.isSameDay(d, date)))
    //   ) {
    //     return true;
    //   }
    //   return false;
    // }
    function closeModal() {
        props.onClose?.();
        setIsOpen(false);
    }
    function openModal() {
        document.activeElement.blur();
        setIsOpen(true);
    }
    const renderLabel = () => {
        if (selectedRelativeDate !== 'CUSTOM') {
            return RelativeDateOptionLabels[selectedRelativeDate];
        }
        if (dateRange) {
            if (dateRange.startDate && dateRange.endDate) {
                return `${adapter.format(dateRange.startDate, 'fullDate')} - ${adapter.format(dateRange.endDate, 'fullDate')}`;
            }
            else if (dateRange.startDate) {
                return `${adapter.format(dateRange.startDate, 'fullDate')} - Present`;
            }
            else if (dateRange.endDate) {
                return `Before ${adapter.format(dateRange.endDate, 'fullDate')}`;
            }
        }
        return 'Custom';
    };
    const handleChange = (date) => {
        setDateRange(date);
        props.onChange?.(date);
    };
    const handleRelativeDateChange = (option) => {
        return () => {
            switch (option) {
                case 'TODAY':
                    handleChange({ startDate: new Date(), endDate: new Date(), relativeDate: 'TODAY' });
                    break;
                case 'YESTERDAY':
                    handleChange({
                        startDate: adapter.addDays(new Date(), -1),
                        endDate: adapter.addDays(new Date(), -1),
                        relativeDate: 'YESTERDAY',
                    });
                    break;
                case 'THIS_WEEK':
                    handleChange({
                        startDate: adapter.startOfWeek(new Date()),
                        endDate: adapter.endOfWeek(new Date()),
                        relativeDate: 'THIS_WEEK',
                    });
                    break;
                case 'LAST_WEEK':
                    handleChange({
                        startDate: adapter.addWeeks(adapter.startOfWeek(new Date()), -1),
                        endDate: adapter.addWeeks(adapter.endOfWeek(new Date()), -1),
                        relativeDate: 'LAST_WEEK',
                    });
                    break;
                case 'THIS_MONTH':
                    handleChange({
                        startDate: adapter.startOfMonth(new Date()),
                        endDate: adapter.endOfMonth(new Date()),
                        relativeDate: 'THIS_MONTH',
                    });
                    break;
                case 'LAST_MONTH':
                    handleChange({
                        startDate: adapter.addMonths(adapter.startOfMonth(new Date()), -1),
                        endDate: adapter.addMonths(adapter.endOfMonth(new Date()), -1),
                        relativeDate: 'LAST_MONTH',
                    });
                    break;
                case 'THIS_YEAR':
                    handleChange({
                        startDate: adapter.startOfYear(new Date()),
                        endDate: adapter.endOfYear(new Date()),
                        relativeDate: 'THIS_YEAR',
                    });
                    break;
                case 'LAST_YEAR':
                    handleChange({
                        startDate: adapter.addYears(adapter.startOfYear(new Date()), -1),
                        endDate: adapter.addYears(adapter.endOfYear(new Date()), -1),
                        relativeDate: 'LAST_YEAR',
                    });
                    break;
                case 'LAST_7_DAYS':
                    handleChange({
                        startDate: adapter.addDays(new Date(), -7),
                        endDate: new Date(),
                        relativeDate: 'LAST_7_DAYS',
                    });
                    break;
                case 'LAST_30_DAYS':
                    handleChange({
                        startDate: adapter.addDays(new Date(), -30),
                        endDate: new Date(),
                        relativeDate: 'LAST_30_DAYS',
                    });
                    break;
                case 'CUSTOM':
                    break;
                default:
                    break;
            }
            setSelectedRelativeDate(option);
            closeModal();
        };
    };
    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        middleware: [offset(10), flip(), shift()],
        whileElementsMounted: autoUpdate,
    });
    return (_jsxs(_Fragment, { children: [_jsx(TextField, { dense: props.dense, id: props?.id, variant: props.variant, label: props.label || 'Date', labelProps: props.variant === 'filled'
                    ? {
                        onClick: () => openModal(),
                    }
                    : {}, inputProps: {
                    ...(props?.inputProps || {}),
                    readOnly: true,
                    onClick: () => openModal(),
                    value: renderLabel(),
                    name: props?.inputProps?.name,
                    tabIndex: -1,
                    ['aria-label']: props.label || 'Date',
                    ['aria-haspopup']: 'dialog',
                    ['aria-expanded']: isOpen,
                    ref: refs.setReference,
                }, endAdornment: _jsx("div", { onClick: () => openModal(), className: `text-primary-400 absolute right-[1px] top-[50%] grid h-[38px] w-[38px] -translate-y-2/4 cursor-pointer place-items-center rounded-br-[6px] rounded-tr-[6px] bg-transparent`, children: _jsx(CalendarIcon, { className: props.dense ? `h-3.5 w-3.5` : `h-5 w-5` }) }), required: props.required, helperText: props.helperText, error: props.error }), isOpen && (_jsx(FloatingOverlay, { lockScroll: true, className: "z-10 bg-black bg-opacity-25", onClick: () => {
                    closeModal();
                }, children: _jsx(FloatingFocusManager, { context: context, modal: true, children: _jsx("div", { ref: refs.setFloating, style: floatingStyles, onClick: (e) => e.stopPropagation(), children: _jsx("div", { className: "z-50 rounded-md bg-white p-4 shadow-md", children: _jsxs("div", { className: "mx-auto flex max-w-md flex-col gap-2", children: [_jsx("section", { className: "flex flex-col gap-2", children: _jsxs("div", { className: "flex", children: [_jsx("button", { type: "button", onClick: handleRelativeDateChange('LAST_7_DAYS'), className: "mr-1 flex-grow rounded border px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-300", children: "Last 7 Days" }), _jsx("button", { type: "button", onClick: handleRelativeDateChange('LAST_30_DAYS'), className: "flex-grow rounded border px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-300", children: "Last 30 Days" })] }) }), _jsxs("section", { className: "flex flex-col gap-2", children: [_jsx("div", { className: "mb-2 flex items-center justify-center border-b uppercase", children: _jsx("h6", { className: "relative top-[8px] bg-white px-4 text-xs font-bold text-gray-700", children: "Last" }) }), _jsxs("div", { className: "flex", children: [_jsx("button", { type: "button", onClick: handleRelativeDateChange('LAST_WEEK'), className: "mr-1 flex-grow rounded border px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-300", children: "Week" }), _jsx("button", { type: "button", onClick: handleRelativeDateChange('LAST_MONTH'), className: "mr-1 flex-grow rounded border px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-300", children: "Month" }), _jsx("button", { type: "button", onClick: handleRelativeDateChange('LAST_YEAR'), className: "flex-grow rounded border px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-300", children: "Year" })] })] }), _jsxs("section", { className: "flex flex-col gap-2", children: [_jsx("div", { className: "mb-2 flex items-center justify-center border-b uppercase", children: _jsx("h6", { className: "relative top-[8px] bg-white px-4 text-xs font-bold text-gray-700", children: "This" }) }), _jsxs("div", { className: "flex", children: [_jsx("button", { type: "button", onClick: handleRelativeDateChange('THIS_WEEK'), className: "mr-1 flex-grow rounded border px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-300", children: "Week" }), _jsx("button", { type: "button", onClick: handleRelativeDateChange('THIS_MONTH'), className: "mr-1 flex-grow rounded border px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-300", children: "Month" }), _jsx("button", { type: "button", onClick: handleRelativeDateChange('THIS_YEAR'), className: "flex-grow rounded border px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-300", children: "Year" })] })] }), _jsx(Tooltip, { text: "Custom date range coming soon", children: _jsx("button", { type: "button", disabled: true, onClick: handleRelativeDateChange('CUSTOM'), className: "mr-1 w-full rounded border px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50", children: "Custom" }) })] }) }) }) }) }))] }));
}
//# sourceMappingURL=RelativeDatePicker.js.map